import { useRef, useState } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Autocomplete } from "@similarweb/ui-components";

import type { Topic } from "app/pages/market-research/api-client/demandSearchTrendsTopicsAPI";
import AutocompleteTopics, {
    AutocompleteTopicsWrapper,
} from "pro-features/components/AutocompleteTopics/AutocompleteTopics";
import type { ITopic } from "pro-features/pages/module start page/src/keyword analysis/topics/types";

const StyledAutocomplete = styled(Autocomplete)<{ isOpened: boolean }>`
    height: 48px;

    .AutoCompleteContainer {
        padding: 0;
        border: none;
        transform: none;
        border-radius: none;
    }

    ${AutocompleteTopicsWrapper} {
        padding-top: 0;
    }

    .input-container {
        border-radius: 0;
        height: 48px;
        padding-top: 6px;
        padding-bottom: 6px;
        border-bottom: ${({ isOpened }) =>
            isOpened ? `1px solid ${colorsPalettes.blue[400]}` : `1px solid ${colorsPalettes.carbon[100]}`};
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

type TopicsHeatmapSearchProps = {
    flattenedTopics: Topic[];
    onTopicSelect: (topic: Topic) => void;
};

export const TopicsHeatmapSearch = ({ flattenedTopics, onTopicSelect }: TopicsHeatmapSearchProps) => {
    const [query, setQuery] = useState("");
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
    const filteredItemsRef = useRef<Topic[]>([]);

    const handleFocus = () => {
        setIsAutocompleteOpen(true);
    };

    const handleBlur = () => {
        setIsAutocompleteOpen(false);
    };

    const getAutocompleteItems = (searchQuery: string) => {
        setQuery(searchQuery);
        const queryRegexp = new RegExp(searchQuery, "i");
        const filtered = flattenedTopics.filter((item: Topic) => item.Level !== 1 && queryRegexp.test(item.Name));
        filteredItemsRef.current = filtered;
        return filtered;
    };

    const handleTopicClick = (topic: ITopic) => {
        onTopicSelect(topic as unknown as Topic);
    };

    const handleEnter = (_event: unknown, selectedItem: Topic) => {
        if (selectedItem) {
            onTopicSelect(selectedItem);
        }
    };

    const renderAutocompleteItems = (data: { listItems: Topic[]; selectedItemId: number }) => {
        if (!data.listItems.length) {
            return null;
        }

        return (
            <AutocompleteTopics
                topics={data.listItems}
                selectedItemId={data.selectedItemId}
                query={query}
                hasQuery={Boolean(query)}
                onClick={handleTopicClick}
            />
        );
    };

    return (
        <StyledAutocomplete
            isOpened={isAutocompleteOpen}
            onFocus={handleFocus}
            onBlur={handleBlur}
            getListItems={getAutocompleteItems}
            renderItems={renderAutocompleteItems}
            overrideEnterFunc={handleEnter}
            data-automation="heatmap-topic-search"
        />
    );
};
